import { useMemo, useState } from "react";
import {
  CalendarDays, CheckCircle2, ChevronRight, ClipboardList, Code2, ExternalLink, File as FileIcon,
  FileText, Flag, Globe, ListTree, MessageSquare, Paperclip, PenTool, Plus, RefreshCcw, Rocket, Search, Users,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { pageStatus } from "@/components/WebProjectPanel";
import { toast } from "sonner";

const fmtD = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; }
};
const fmtDs = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" }).replace(".", ""); } catch { return iso; }
};
const relTime = (iso) => {
  if (!iso) return "";
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return "à l'instant";
  if (h < 24) return `il y a ${h} heure${h > 1 ? "s" : ""}`;
  const d = Math.floor(h / 24);
  return `il y a ${d} jour${d > 1 ? "s" : ""}`;
};
const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";

export const STEPS = [
  ["Arborescence", ListTree], ["Maquette", PenTool], ["Contenus", FileText],
  ["Développement", Code2], ["SEO", Search], ["Validation", CheckCircle2], ["Mise en ligne", Rocket],
];
export const STAGE_STEP = {
  creation: 0, cahier_des_charges: 0, design: 1, collecte: 2, developpement: 3, seo: 4,
  tests: 5, validation_finale: 5, prete_mise_en_ligne: 6, en_ligne: 6, recette: 6, cloture: 6,
};

const PRIO = {
  urgente: { label: "Haute", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  haute: { label: "Haute", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  normale: { label: "Moyenne", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  basse: { label: "Basse", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const DOC_STYLE = [
  [/\.pdf$/i, "bg-red-50 text-red-500"], [/\.fig$/i, "bg-violet-50 text-violet-500"],
  [/\.docx?$/i, "bg-blue-50 text-blue-500"], [/\.xlsx?$/i, "bg-emerald-50 text-emerald-600"],
  [/\.zip$/i, "bg-amber-50 text-amber-600"],
];
const docCls = (name = "") => (DOC_STYLE.find(([re]) => re.test(name)) || [null, "bg-neutral-100 text-neutral-400"])[1];

const KIND_LABEL = { moodboard: "Moodboard", inspiration: "Inspiration", palette: "Palette", typo: "Typographie", maquette: "Maquette", autre: "Fichier" };

export const stepDates = (p) => {
  const s = p?.start_date, e = p?.delivery_date;
  if (!s || !e) return STEPS.map(() => null);
  const t0 = new Date(s + "T12:00:00").getTime();
  const t1 = new Date(e + "T12:00:00").getTime();
  if (!(t1 > t0)) return STEPS.map(() => null);
  const seg = (t1 - t0) / STEPS.length;
  return STEPS.map((_, i) => {
    const from = new Date(t0 + i * seg);
    const to = new Date(t0 + (i + 1) * seg - 86400000);
    const f = from.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }).replace(".", "");
    const t = to.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }).replace(".", "");
    return f === t ? f : `${f} – ${t}`;
  });
};

function Sidebar({ p, act, setTab }) {
  const tasks = (p.tasks || []).filter((t) => t.status !== "termine");
  const docs = useMemo(() => {
    const assets = ((p.design || {}).assets || []).map((a) => ({ id: a.id, name: a.name, meta: `${KIND_LABEL[a.kind] || "Fichier"} · ${fmtDs(a.at)}`, url: a.url }));
    const versions = ((p.design || {}).versions || []).map((v) => ({ id: v.id, name: `Maquette ${v.label}`, meta: `Version · ${fmtDs(v.at)}`, url: (v.links || [])[0] }));
    return [...assets, ...versions];
  }, [p]);
  const messages = (p.messages || []).slice(-4).reverse();
  const collabs = Array.isArray(p.collaborators) ? p.collaborators : String(p.collaborators || "").split(/[,\/]/);
  const team = [p.webmaster, ...collabs].map((s) => String(s || "").trim()).filter(Boolean);

  const toggleTask = async (t) => {
    try {
      await act(() => api.patch(`/web/projects/${p.id}/tasks/${t.id}`, { status: "termine" }), "Tâche terminée");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-0 overflow-y-auto space-y-3 pr-0.5" data-testid="wpp-sidebar">
      {/* Équipe projet */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5">
        <h3 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5 mb-2.5">
          <Users className="w-3.5 h-3.5 text-neutral-400" /> Équipe projet
          <span className="ml-auto text-[10px] font-semibold text-neutral-400">{team.length}</span>
        </h3>
        {team.length === 0 && <p className="text-[11px] text-neutral-400">Aucun membre assigné.</p>}
        <div className="flex flex-wrap gap-1.5">
          {team.map((n) => (
            <span key={n} title={n} data-testid={`wpp-team-${n}`}
              className="inline-flex items-center gap-1.5 border border-neutral-100 rounded-full pl-1 pr-2.5 py-1">
              <span className="w-6 h-6 rounded-full bg-[#DBEAFE] text-[#1D4ED8] text-[9px] font-bold flex items-center justify-center">{initialsOf(n)}</span>
              <span className="text-[11px] font-medium text-neutral-600">{n}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Actions à venir */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5">
        <h3 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5 mb-1">
          <ClipboardList className="w-3.5 h-3.5 text-neutral-400" /> Actions à venir
          <span className="text-[10px] font-semibold text-neutral-400">{tasks.length}</span>
          <button onClick={() => setTab("taches")} className="ml-auto text-neutral-300 hover:text-neutral-600" data-testid="wpp-actions-all"><ChevronRight className="w-3.5 h-3.5" /></button>
        </h3>
        {tasks.length === 0 && <p className="text-[11px] text-neutral-400 py-1">Aucune action en attente.</p>}
        {tasks.slice(0, 6).map((t) => {
          const pr = PRIO[t.priority] || PRIO.normale;
          return (
            <div key={t.id} className="flex items-start gap-2 py-2 border-b border-neutral-50 last:border-0" data-testid={`wpp-action-${t.id}`}>
              <button onClick={() => toggleTask(t)} data-testid={`wpp-action-check-${t.id}`}
                className="mt-0.5 w-4 h-4 rounded-full border-2 border-neutral-300 hover:border-[#3B82F6] transition-colors shrink-0" title="Marquer terminée" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[#0F172A] leading-snug">{t.title}</p>
                <span className={`inline-block mt-1 text-[9px] font-semibold border rounded-full px-2 py-px ${pr.cls}`}>{pr.label}</span>
              </div>
              <span className="text-[10px] text-neutral-400 shrink-0 mt-0.5">{t.due_date ? fmtDs(t.due_date) : ""}</span>
            </div>
          );
        })}
      </div>

      {/* Documents du projet */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5">
        <h3 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5 mb-1">
          <Paperclip className="w-3.5 h-3.5 text-neutral-400" /> Documents du projet
          <span className="text-[10px] font-semibold text-neutral-400">{docs.length}</span>
          <button onClick={() => setTab("fichiers")} data-testid="wpp-docs-add"
            className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-[#6D28D9] hover:underline">
            <Plus className="w-3 h-3" /> Ajouter
          </button>
        </h3>
        {docs.length === 0 && <p className="text-[11px] text-neutral-400 py-1">Aucun document pour l'instant.</p>}
        {docs.slice(0, 7).map((d) => (
          <a key={d.id} href={d.url || undefined} target="_blank" rel="noreferrer" data-testid={`wpp-doc-${d.id}`}
            className="flex items-center gap-2.5 py-1.5 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 -mx-1 px-1 rounded-lg transition-colors">
            <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${docCls(d.name)}`}><FileIcon className="w-3.5 h-3.5" /></span>
            <span className="min-w-0">
              <span className="block text-[11px] font-semibold text-[#0F172A] truncate">{d.name}</span>
              <span className="block text-[9px] text-neutral-400">{d.meta}</span>
            </span>
          </a>
        ))}
      </div>

      {/* Échanges récents */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5">
        <h3 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5 mb-1">
          <MessageSquare className="w-3.5 h-3.5 text-neutral-400" /> Échanges récents
          <button onClick={() => setTab("echanges")} className="ml-auto text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="wpp-msgs-all">Voir tous ›</button>
        </h3>
        {messages.length === 0 && <p className="text-[11px] text-neutral-400 py-1">Aucun échange pour le moment.</p>}
        {messages.map((m) => (
          <div key={m.id} className="flex items-start gap-2.5 py-2 border-b border-neutral-50 last:border-0" data-testid={`wpp-msg-${m.id}`}>
            <span className={`w-7 h-7 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0 ${m.by_client ? "bg-emerald-100 text-emerald-700" : "bg-[#DBEAFE] text-[#1D4ED8]"}`}>{initialsOf(m.by)}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] flex items-center justify-between gap-2">
                <b className="text-neutral-700">{m.by}</b>
                <span className="text-neutral-300 shrink-0">{relTime(m.at)}</span>
              </p>
              <p className="text-[11px] text-neutral-500 leading-snug line-clamp-2">{m.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ApercuTab({ p, act, patch, setTab, client }) {
  const [pageName, setPageName] = useState("");
  const pages = p.pages || [];
  const tasks = p.tasks || [];
  const tasksDone = tasks.filter((t) => t.status === "termine").length;
  const stepIdx = STAGE_STEP[p.stage] ?? 0;
  const dates = stepDates(p);
  const deadlineDays = p.delivery_date ? Math.ceil((new Date(p.delivery_date + "T23:59:59") - Date.now()) / 86400000) : null;
  const siteUrl = p.preprod_url || p.production_url || "";
  const clientSince = client?.created_at ? new Date(client.created_at).getFullYear() : null;

  const kpis = [
    { id: "avancement", label: "Avancement global", icon: RefreshCcw, bg: "#E8F0FE", fg: "#1D4ED8", value: `${p.progress} %`, bar: p.progress },
    { id: "livraison", label: "Date de livraison", icon: CalendarDays, bg: "#EDE9FE", fg: "#6D28D9", value: fmtD(p.delivery_date), sub: deadlineDays != null ? (deadlineDays < 0 ? "En retard" : deadlineDays === 0 ? "Aujourd'hui" : `Dans ${deadlineDays} jours`) : "" },
    { id: "taches", label: "Tâches terminées", icon: CheckCircle2, bg: "#DCFCE7", fg: "#15803D", value: `${tasksDone} / ${tasks.length}`, sub: tasks.length ? `${Math.round((tasksDone / tasks.length) * 100)} %` : "" },
    { id: "commentaires", label: "Commentaires", icon: MessageSquare, bg: "#E0F2FE", fg: "#0369A1", value: (p.messages || []).length, sub: "sur ce projet" },
  ];

  const addPage = async () => {
    if (!pageName.trim()) return;
    try {
      await act(() => api.post(`/web/projects/${p.id}/pages`, { name: pageName.trim() }), "Page ajoutée à l'arborescence");
      setPageName("");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_310px] gap-3" data-testid="wpp-apercu">
      {/* Colonne principale */}
      <div className="min-h-0 overflow-y-auto space-y-3 pr-0.5">
        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="wpp-kpis">
          {kpis.map((c) => (
            <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5" data-testid={`wpp-kpi-${c.id}`}>
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: c.bg }}>
                  <c.icon className="w-4 h-4" style={{ color: c.fg }} />
                </span>
                <p className="text-[11px] font-medium text-neutral-500 leading-tight">{c.label}</p>
              </div>
              <p className="font-heading text-xl font-bold mt-2 leading-tight" data-testid={`wpp-kpi-${c.id}-value`}>{c.value}</p>
              {c.bar != null && (
                <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden mt-1.5">
                  <div className="h-full bg-[#3B82F6] rounded-full transition-all" style={{ width: `${c.bar}%` }} />
                </div>
              )}
              {c.sub && <p className="text-[10px] text-neutral-400 mt-1">{c.sub}</p>}
            </div>
          ))}
        </div>

        {/* Étapes du projet */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-4" data-testid="wpp-steps">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-4 h-4 text-neutral-400" />
            <h3 className="font-heading font-bold text-sm">Étapes du projet</h3>
            <button onClick={() => setTab("planning")} className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline" data-testid="wpp-planning-link">
              Voir le planning complet ›
            </button>
          </div>
          <div className="flex items-start">
            {STEPS.map(([label, Icon], i) => {
              const state = i < stepIdx ? "done" : i === stepIdx ? "current" : "todo";
              return (
                <div key={label} className="flex-1 flex flex-col items-center relative min-w-0" data-testid={`wpp-step-${i}`}>
                  {i > 0 && <span className={`absolute top-[15px] right-1/2 w-full h-0.5 ${i <= stepIdx ? "bg-[#3B82F6]" : "bg-neutral-200"}`} />}
                  <span className={`relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                    state === "done" ? "bg-[#3B82F6] border-[#3B82F6]" : state === "current" ? "bg-white border-[#3B82F6] shadow-[0_0_0_4px_#DBEAFE]" : "bg-white border-neutral-200"}`}>
                    {state === "done" ? <CheckCircle2 className="w-4 h-4 text-white" />
                      : <Icon className={`w-3.5 h-3.5 ${state === "current" ? "text-[#1D4ED8]" : "text-neutral-300"}`} />}
                  </span>
                  <span className={`mt-1.5 text-[10px] font-semibold text-center leading-tight truncate w-full px-0.5 ${state === "todo" ? "text-neutral-400" : "text-[#0F172A]"}`}>
                    {i + 1}. {label}
                  </span>
                  <span className={`text-[9px] ${state === "done" ? "text-emerald-600" : state === "current" ? "text-[#1D4ED8] font-semibold" : "text-neutral-300"}`}>
                    {state === "done" ? "Terminée" : state === "current" ? "En cours" : "À venir"}
                  </span>
                  {dates[i] && <span className="text-[9px] text-neutral-400">{dates[i]}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Aperçu du site + Pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-4" data-testid="wpp-preview">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-neutral-400" />
              <h3 className="font-heading font-bold text-sm">Aperçu du site</h3>
              {siteUrl && (
                <a href={siteUrl} target="_blank" rel="noreferrer" className="ml-auto text-neutral-300 hover:text-neutral-600" data-testid="wpp-preview-open" title="Ouvrir">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            <div className="rounded-xl border border-neutral-200 overflow-hidden bg-neutral-50">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border-b border-neutral-100">
                <span className="w-2 h-2 rounded-full bg-[#FCA5A5]" /><span className="w-2 h-2 rounded-full bg-[#FCD34D]" /><span className="w-2 h-2 rounded-full bg-[#86EFAC]" />
                <span className="ml-2 flex-1 text-[9px] text-neutral-400 bg-neutral-50 border border-neutral-100 rounded-md px-2 py-0.5 truncate">
                  {siteUrl || `preprod.${(p.client_name || "projet").toLowerCase().replace(/[^a-z]/g, "")}.ch`}
                </span>
              </div>
              {p.preview_url ? (
                <img src={p.preview_url} alt="" className="w-full h-44 object-cover object-top" data-testid="wpp-preview-img" />
              ) : (
                <div className="w-full h-44 flex items-center justify-center text-neutral-300" data-testid="wpp-preview-img"><Globe className="w-8 h-8" /></div>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-4 flex flex-col" data-testid="wpp-pages">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-neutral-400" />
              <h3 className="font-heading font-bold text-sm">Pages du site</h3>
              <span className="text-[10px] font-semibold text-neutral-400">{pages.length}</span>
              <button onClick={() => setTab("pages")} className="ml-auto text-[11px] font-semibold text-[#6D28D9] hover:underline" data-testid="wpp-pages-all">
                + Ajouter une page
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {pages.length === 0 && <p className="text-[11px] text-neutral-400 py-3">Aucune page — définissez l'arborescence du site.</p>}
              {pages.map((pg) => {
                const ps = pageStatus(pg);
                return (
                  <div key={pg.id} className="flex items-center gap-2.5 py-1.5 border-b border-neutral-50 last:border-0" data-testid={`wpp-page-${pg.id}`}>
                    <FileText className="w-3.5 h-3.5 text-neutral-300 shrink-0" />
                    <span className="flex-1 min-w-0 text-xs font-medium text-[#0F172A] truncate">{pg.name}</span>
                    <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${ps.cls}`}>{ps.label}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pt-2 mt-1 border-t border-neutral-100">
              <input value={pageName} onChange={(e) => setPageName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPage()}
                placeholder="+ Ajouter une page…" data-testid="wpp-page-add-input"
                className="flex-1 text-xs py-1 focus:outline-none placeholder:text-neutral-300" />
              <button onClick={addPage} data-testid="wpp-page-add-btn" className="text-[11px] font-semibold text-[#0369A1] hover:underline shrink-0">Ajouter</button>
            </div>
          </div>
        </div>
      </div>

      <Sidebar p={p} act={act} setTab={setTab} client={client} clientSince={clientSince} />
    </div>
  );
}

export function PlanningTab({ p }) {
  const stepIdx = STAGE_STEP[p.stage] ?? 0;
  const dates = stepDates(p);
  const milestones = [
    { id: "start", label: "Démarrage du projet", date: p.start_date, icon: Flag, bg: "#E8F0FE", fg: "#1D4ED8" },
    { id: "delivery", label: "Livraison prévue", date: p.delivery_date, icon: CalendarDays, bg: "#EDE9FE", fg: "#6D28D9" },
    { id: "golive", label: "Mise en ligne", date: p.golive_date, icon: Rocket, bg: "#DCFCE7", fg: "#15803D" },
  ];
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-0.5" data-testid="wpp-planning">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        {milestones.map((m) => (
          <div key={m.id} className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5 flex items-center gap-3" data-testid={`wpp-ms-${m.id}`}>
            <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: m.bg }}>
              <m.icon className="w-4.5 h-4.5" style={{ color: m.fg }} />
            </span>
            <div>
              <p className="text-[11px] text-neutral-500">{m.label}</p>
              <p className="font-heading text-base font-bold">{fmtD(m.date)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-4">
        <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-neutral-400" /> Phases du projet</h3>
        {STEPS.map(([label, Icon], i) => {
          const state = i < stepIdx ? "done" : i === stepIdx ? "current" : "todo";
          return (
            <div key={label} className="flex items-center gap-3 py-2.5 border-b border-neutral-50 last:border-0" data-testid={`wpp-plan-step-${i}`}>
              <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 ${
                state === "done" ? "bg-[#3B82F6] border-[#3B82F6]" : state === "current" ? "bg-white border-[#3B82F6]" : "bg-white border-neutral-200"}`}>
                {state === "done" ? <CheckCircle2 className="w-4 h-4 text-white" /> : <Icon className={`w-3.5 h-3.5 ${state === "current" ? "text-[#1D4ED8]" : "text-neutral-300"}`} />}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold ${state === "todo" ? "text-neutral-400" : "text-[#0F172A]"}`}>{i + 1}. {label}</p>
                {dates[i] && <p className="text-[10px] text-neutral-400">{dates[i]}</p>}
              </div>
              <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 ${
                state === "done" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : state === "current" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-neutral-100 text-neutral-500 border-neutral-200"}`}>
                {state === "done" ? "Terminée" : state === "current" ? "En cours" : "À venir"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
